// 図形本体の「store を伴わないレイアウト変化」を 1 つの購読へまとめる共通モジュール。
//
// 他参加者の選択枠（canvas-remote-selections-layer）とキャレット層（canvas-remote-carets-layer）は、
// テキスト図形の枠の高さに同じ実描画高さ（offsetHeight）を使う。両者が別々の契機で再計測すると、
// 再レイアウトの瞬間に「新しいキャレットが古い枠の外へ出る」ことがあるため、観測をここへ一本化して
// 同じ通知で同じ値を読ませる。
//
// 検知する契機は 2 つ:
// - ResizeObserver: 本体の大きさが変わったとき（テキスト図形の本文追加など）。
//   通知の回数ではなく offsetWidth / offsetHeight の変化で判定する（observe() 直後の初回通知でも、
//   購読開始から通知までに大きさが変わっていれば取りこぼさない）。
// - document.fonts: Web フォントの読み込み完了。本体の大きさが変わらずに折り返しだけが変わる
//   （付箋など）場合は ResizeObserver では検知できない。
//
// どちらの API も無い環境（jsdom）では、その部分を行わない（例外にしない）。

/** 1 つの図形本体に対する観測状態。複数の購読者（両レイヤ・複数の参加者）が 1 要素を共有する。 */
interface ObservedEntry {
  width: number;
  height: number;
  readonly listeners: Set<() => void>;
}

const observedEntries = new Map<Element, ObservedEntry>();
/** ResizeObserver はモジュール内で 1 つを共有する（要素ごとに作らない）。 */
let sharedResizeObserver: ResizeObserver | null = null;

function readElementSize(element: Element): { width: number; height: number } {
  if (element instanceof HTMLElement) {
    return { width: element.offsetWidth, height: element.offsetHeight };
  }
  const rect = element.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

function notifyIfResized(element: Element): void {
  const entry = observedEntries.get(element);
  if (!entry) return;
  const size = readElementSize(element);
  if (entry.width === size.width && entry.height === size.height) return;
  entry.width = size.width;
  entry.height = size.height;
  // 通知中に購読解除されても走査が壊れないよう、複製してから呼ぶ。
  for (const listener of [...entry.listeners]) listener();
}

function ensureResizeObserver(): ResizeObserver | null {
  if (typeof ResizeObserver === "undefined") return null;
  if (!sharedResizeObserver) {
    sharedResizeObserver = new ResizeObserver((records) => {
      for (const record of records) notifyIfResized(record.target);
    });
  }
  return sharedResizeObserver;
}

/** 観測対象が 1 つも無くなったら ResizeObserver 自体を破棄する（次の購読で作り直す）。 */
function releaseResizeObserverIfIdle(): void {
  if (observedEntries.size > 0 || !sharedResizeObserver) return;
  sharedResizeObserver.disconnect();
  sharedResizeObserver = null;
}

/** document.fonts（FontFaceSet）を、存在する環境でだけ取り出す。 */
function resolveFontFaceSet(element: Element): FontFaceSet | null {
  const doc = element.ownerDocument as Document & { readonly fonts?: FontFaceSet | undefined };
  return doc.fonts ?? null;
}

/**
 * 図形本体のレイアウト変化（大きさの変化・フォント読み込み完了）を購読する。
 * 戻り値の関数で購読を解除する。同じ要素を複数の購読者が観測でき、全員が解除して初めて
 * ResizeObserver の観測も外れる。
 */
export function observeObjectLayout(element: HTMLElement, onChange: () => void): () => void {
  let active = true;
  const listener = (): void => {
    if (active) onChange();
  };

  let entry = observedEntries.get(element);
  if (!entry) {
    const size = readElementSize(element);
    entry = { width: size.width, height: size.height, listeners: new Set() };
    observedEntries.set(element, entry);
    ensureResizeObserver()?.observe(element);
  }
  entry.listeners.add(listener);

  const fonts = resolveFontFaceSet(element);
  const onFontsLoadingDone = (): void => listener();
  if (fonts) {
    // ready は Promise のため、解除後に解決することがある。active フラグで空振りさせる。
    // 既に読み込みが済んでいる（status が "loaded"）場合は、購読開始時点のレイアウトが
    // そのフォントで組まれているため通知しない。購読のたびに解決済みの ready で 1 回鳴ると、
    // 観測対象が作り直される状況で「通知 → 再計測 → 再購読」が繰り返し起きる余地が残る。
    if (fonts.status !== "loaded" && typeof fonts.ready?.then === "function") {
      void fonts.ready.then(() => listener()).catch(() => undefined);
    }
    if (typeof fonts.addEventListener === "function") {
      fonts.addEventListener("loadingdone", onFontsLoadingDone);
    }
  }

  return () => {
    if (!active) return;
    active = false;
    if (fonts && typeof fonts.removeEventListener === "function") {
      fonts.removeEventListener("loadingdone", onFontsLoadingDone);
    }
    const current = observedEntries.get(element);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size > 0) return;
    observedEntries.delete(element);
    sharedResizeObserver?.unobserve(element);
    releaseResizeObserverIfIdle();
  };
}
